import {
  Alert,
  Button,
  Checkbox,
  EmptyState,
  KeyValue,
  List,
  ListRow,
  MoreMenu,
  PageHeader,
  Section,
  Skeleton,
  Text,
  useToast,
  type MenuItem,
} from '@ui';
import { Fragment, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { BackLink } from '../../../app/BackLink';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { errorMessage } from '../../../lib/errors';
import { formatDate, formatDateTime } from '../../../lib/format';
import { useSession } from '../../identity/session';
import { useCreateInvoice, useSale, useSaleAction, type PaymentMethod, type Sale } from '../api';
import {
  InvoiceRecipientDialog,
  type InvoiceRecipient,
} from '../components/InvoiceRecipientDialog';
import { PaymentDialog } from '../components/PaymentDialog';
import { ReferrerDialog, useReferrerName } from '../components/Referrer';
import { InvoiceBadge, PaymentBadge, SaleStatusBadge } from '../components/SaleBadges';
import { SaleFormDialog } from '../components/SaleFormDialog';
import { TasksCard } from '../../tasks/components/TasksCard';
import { formatEuro } from '../format';
import styles from './SalesPages.module.css';

type Open = 'edit' | 'pay' | 'cancel' | 'deliver' | 'referrer' | 'recipient' | null;

/** One sale with its lines, payment and invoices (SAL-001 – SAL-006). */
export function SaleDetailPage() {
  const { t } = useTranslation();
  const { saleId = '' } = useParams();
  const sale = useSale(saleId);
  if (sale.error) {
    return (
      <EmptyState
        size="page"
        level={1}
        title={errorMessage(sale.error)}
        actions={<BackLink to="/verkauf" label={t('sales.tabs.sales')} />}
      />
    );
  }
  if (!sale.data) {
    return (
      <div role="status" aria-label={t('common.loading')} className={styles.page}>
        <Skeleton width="40%" height="2rem" />
        <Skeleton lines={8} />
      </div>
    );
  }
  return <SaleDetail sale={sale.data} />;
}

function SaleDetail({ sale }: { sale: Sale }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = useSession();
  const [open, setOpen] = useState<Open>(null);
  const [confirmReturn, setConfirmReturn] = useState(false);
  const action = useSaleAction();
  const createInvoice = useCreateInvoice();
  const close = () => {
    action.reset();
    setConfirmReturn(false);
    setOpen(null);
  };
  const write = can('sales.write');
  const referrerName = useReferrerName();
  const active = sale.status !== 'cancelled';
  const invoice = sale.invoices.find((i) => i.kind === 'invoice' && i.status !== 'cancelled');
  const finalInvoice = invoice?.status === 'final';
  const editable = write && (sale.status === 'draft' || sale.status === 'ordered') && !finalInvoice;
  const marketDay = sale.market_day;
  const title = sale.customer
    ? t('sales.detail.titleCustomer', { name: sale.customer.name })
    : t('sales.detail.titleAnonymous');
  const run = (a: Parameters<typeof action.mutate>[0], message: string) =>
    action.mutate(a, {
      onSuccess: () => {
        toast.show(message);
        close();
      },
    });

  const canDeliver = write && (sale.status === 'draft' || sale.status === 'ordered');
  const canPay = write && sale.status !== 'draft' && active && !sale.payment.paid;
  const canInvoice = can('invoices.write') && active && sale.status !== 'draft' && !invoice;
  // One primary action: the next step of the sale (deliver, invoice, get paid).
  const primary = canDeliver ? 'deliver' : canInvoice ? 'invoice' : canPay ? 'pay' : null;

  const createInvoiceFor = (recipient: InvoiceRecipient) =>
    createInvoice.mutate(
      { sale_id: sale.id, ...recipient },
      {
        onSuccess: (i) => {
          toast.show(t('sales.detail.invoiceCreated'));
          void navigate(`/verkauf/rechnungen/${i.id}`);
        },
      },
    );
  // Without a customer (market) the recipient is asked first (MKT-017).
  const invoiceNow = () => (sale.customer ? createInvoiceFor({}) : setOpen('recipient'));

  // Marking as paid is reversible: undo removes the payment again.
  const pay = (method: PaymentMethod, on: string) =>
    action.mutate(
      { id: sale.id, action: 'pay', method, on },
      {
        onSuccess: () => {
          close();
          toast.show(t('sales.detail.paid'), 'success', {
            label: t('common.undo'),
            onClick: () =>
              action.mutate(
                { id: sale.id, action: 'unpay' },
                { onError: (e) => toast.show(errorMessage(e), 'danger') },
              ),
          });
        },
      },
    );

  const unpay = () => {
    const { method, paid_on } = sale.payment;
    action.mutate(
      { id: sale.id, action: 'unpay' },
      {
        onSuccess: () =>
          toast.show(
            t('sales.detail.unpaid'),
            'success',
            method
              ? {
                  label: t('common.undo'),
                  onClick: () =>
                    action.mutate(
                      { id: sale.id, action: 'pay', method, on: paid_on ?? undefined },
                      { onError: (e) => toast.show(errorMessage(e), 'danger') },
                    ),
                }
              : undefined,
          ),
      },
    );
  };

  const steps: MenuItem[] = [
    ...(write && sale.status === 'draft'
      ? [
          {
            key: 'order',
            label: t('sales.detail.order'),
            onSelect: () => run({ id: sale.id, action: 'order' }, t('sales.detail.ordered')),
          },
        ]
      : []),
    ...(canPay && primary !== 'pay'
      ? [{ key: 'pay', label: t('sales.detail.pay'), onSelect: () => setOpen('pay') }]
      : []),
    ...(canInvoice && primary !== 'invoice'
      ? [{ key: 'invoice', label: t('sales.detail.createInvoice'), onSelect: invoiceNow }]
      : []),
  ];
  const changes: MenuItem[] = [
    ...(editable
      ? [{ key: 'edit', label: t('sales.detail.edit'), onSelect: () => setOpen('edit') }]
      : []),
    ...(write && sale.status === 'delivered' && sale.payment.paid && !marketDay
      ? [{ key: 'unpay', label: t('sales.detail.unpay'), onSelect: unpay }]
      : []),
    // Drafts and orders change it in the form; afterwards on its own (#26).
    ...(write && !editable
      ? [
          {
            key: 'referrer',
            label: t('sales.referrer.change'),
            onSelect: () => setOpen('referrer'),
          },
        ]
      : []),
  ];
  const more: MenuItem[] = [
    ...steps,
    ...changes.map((item, i) => ({ ...item, separated: i === 0 && steps.length > 0 })),
    ...(write && active && !finalInvoice
      ? [
          {
            key: 'cancel',
            label: t('sales.detail.cancel'),
            tone: 'danger' as const,
            separated: steps.length + changes.length > 0,
            onSelect: () => setOpen('cancel'),
          },
        ]
      : []),
  ];

  const meta: ReactNode[] = [
    formatDate(sale.sold_on),
    <SaleStatusBadge key="status" status={sale.status} />,
    ...(sale.status !== 'draft' && active
      ? [
          <PaymentBadge
            key="payment"
            status={sale.status}
            paid={sale.payment.paid}
            method={sale.payment.method}
          />,
        ]
      : []),
    ...(invoice ? [invoice.number ?? t('sales.invoice.badge.draft')] : []),
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        back={
          marketDay ? (
            <BackLink to={`/verkauf/markttage/${marketDay.id}`} label={marketDay.name} />
          ) : (
            <BackLink to="/verkauf" label={t('sales.tabs.sales')} />
          )
        }
        title={title}
        meta={meta.map((m, i) => (
          <Fragment key={i}>
            {i > 0 && ' · '}
            {m}
          </Fragment>
        ))}
        actions={
          <>
            {primary === 'deliver' && (
              <Button onClick={() => setOpen('deliver')}>{t('sales.detail.deliver')}</Button>
            )}
            {primary === 'pay' && (
              <Button onClick={() => setOpen('pay')}>{t('sales.detail.pay')}</Button>
            )}
            {primary === 'invoice' && (
              <Button loading={createInvoice.isPending} onClick={invoiceNow}>
                {t('sales.detail.createInvoice')}
              </Button>
            )}
            {more.length > 0 && <MoreMenu label={t('common.moreActions')} items={more} />}
          </>
        }
      />
      {(action.error || createInvoice.error) && open === null && (
        <Alert tone="danger">{errorMessage(action.error ?? createInvoice.error)}</Alert>
      )}
      {active && sale.status !== 'delivered' && sale.warnings.length > 0 && (
        <Alert tone="warning" title={t('sales.warnings.title')}>
          {sale.warnings
            .map((w) =>
              t('sales.warnings.line', {
                article: w.article.name,
                requested: w.requested,
                available: Math.max(0, w.available),
              }),
            )
            .join(' · ')}
        </Alert>
      )}

      <div className={styles.columns}>
        <div className={styles.stack}>
          <Section title={t('sales.detail.lines')}>
            <List>
              {sale.lines.map((l) => (
                <ListRow
                  key={`${l.article.id}-${l.batch?.id ?? ''}-${l.unit_price_cents}`}
                  title={l.article.name}
                  description={
                    <>
                      {l.quantity} × {formatEuro(l.unit_price_cents)}
                      {l.discount_cents > 0 &&
                        ` · ${t('sales.detail.lineDiscount', { amount: formatEuro(l.discount_cents) })}`}
                      {l.batch && (
                        <>
                          {' · '}
                          {t('sales.detail.batch')}{' '}
                          <Link to={`/ernte/${l.batch.id}`}>{l.batch.name}</Link>
                        </>
                      )}
                    </>
                  }
                  trailing={
                    <Text as="span" numeric>
                      {formatEuro(l.total_cents)}
                    </Text>
                  }
                />
              ))}
              {sale.discount_cents > 0 && (
                <>
                  <ListRow
                    title={t('sales.form.subtotal')}
                    trailing={
                      <Text as="span" numeric>
                        {formatEuro(sale.subtotal_cents)}
                      </Text>
                    }
                  />
                  <ListRow
                    title={t('sales.form.discount')}
                    trailing={
                      <Text as="span" numeric>
                        −{formatEuro(sale.discount_cents)}
                      </Text>
                    }
                  />
                </>
              )}
              <ListRow
                title={<Text weight="semibold">{t('sales.form.total')}</Text>}
                trailing={
                  <Text as="span" numeric weight="semibold">
                    {formatEuro(sale.total_cents)}
                  </Text>
                }
              />
            </List>
          </Section>
          {can('invoices.read') && sale.invoices.length > 0 && (
            <Section title={t('sales.detail.invoices')}>
              <List>
                {sale.invoices.map((i) => (
                  <ListRow
                    key={i.id}
                    title={i.number ?? t('sales.invoice.badge.draft')}
                    href={`/verkauf/rechnungen/${i.id}`}
                    trailing={
                      <InvoiceBadge
                        status={i.status}
                        paid={sale.payment.paid}
                        overdue={false}
                        cancellation={i.kind === 'cancellation'}
                      />
                    }
                  />
                ))}
              </List>
            </Section>
          )}
        </div>

        <div className={styles.stack}>
          <Section
            title={t('sales.detail.details')}
            footer={
              sale.changed.at &&
              (sale.changed.by
                ? t('apiary.colony.changed', {
                    date: formatDateTime(sale.changed.at),
                    name: sale.changed.by,
                  })
                : t('apiary.colony.changedNoName', { date: formatDateTime(sale.changed.at) }))
            }
          >
            <KeyValue
              items={[
                {
                  key: 'customer',
                  label: t('sales.form.customer'),
                  value: sale.customer ? (
                    <Link to={`/kunden/${sale.customer.id}`}>{sale.customer.name}</Link>
                  ) : (
                    t('sales.detail.anonymous')
                  ),
                },
                {
                  key: 'marketDay',
                  label: t('sales.detail.marketDay'),
                  value: marketDay && (
                    <Link to={`/verkauf/markttage/${marketDay.id}`}>{marketDay.name}</Link>
                  ),
                },
                { key: 'date', label: t('sales.form.date'), value: formatDate(sale.sold_on) },
                {
                  key: 'delivered',
                  label: t('sales.detail.deliveredOn'),
                  value: sale.delivered_on && formatDate(sale.delivered_on),
                },
                {
                  key: 'cancelled',
                  label: t('sales.detail.cancelledOn'),
                  value: sale.cancelled_on && formatDate(sale.cancelled_on),
                },
                {
                  key: 'handover',
                  label: t('sales.form.handover'),
                  value: sale.handover && t(`sales.handover.${sale.handover}`),
                },
                {
                  key: 'referredBy',
                  label: t('sales.form.referredBy'),
                  value: referrerName(sale.referred_by),
                },
                {
                  key: 'payment',
                  label: t('sales.detail.payment'),
                  value: sale.payment.paid
                    ? t('sales.detail.paidOn', {
                        date: sale.payment.paid_on ? formatDate(sale.payment.paid_on) : '–',
                        method: sale.payment.method ? t(`sales.method.${sale.payment.method}`) : '',
                      })
                    : t('sales.payment.open'),
                },
                {
                  key: 'notes',
                  label: t('sales.form.notes'),
                  value: sale.notes && <span className={styles.notes}>{sale.notes}</span>,
                },
              ]}
            />
          </Section>
          <TasksCard
            filter={{ sale_id: sale.id }}
            context={{
              saleId: sale.id,
              ...(sale.customer ? { customerId: sale.customer.id } : {}),
              label: title,
            }}
          />
        </div>
      </div>

      {open === 'edit' && <SaleFormDialog open sale={sale} onClose={close} />}
      {open === 'referrer' && <ReferrerDialog sale={sale} onClose={close} />}
      {open === 'recipient' && (
        <InvoiceRecipientDialog
          onClose={() => {
            createInvoice.reset();
            close();
          }}
          totalCents={sale.total_cents}
          pending={createInvoice.isPending}
          error={createInvoice.error}
          onSubmit={createInvoiceFor}
        />
      )}
      {open === 'pay' && (
        <PaymentDialog
          open
          onClose={close}
          pending={action.isPending}
          error={action.error}
          onSubmit={pay}
        />
      )}
      <ConfirmDialog
        open={open === 'deliver'}
        onClose={close}
        tone="default"
        title={t('sales.detail.deliverTitle')}
        description={
          sale.warnings.length > 0
            ? t('sales.detail.deliverWarning')
            : t('sales.detail.deliverText')
        }
        confirmLabel={t('sales.detail.deliver')}
        pending={action.isPending}
        error={action.error ? errorMessage(action.error) : undefined}
        onConfirm={() => run({ id: sale.id, action: 'deliver' }, t('sales.detail.delivered'))}
      />
      <ConfirmDialog
        open={open === 'cancel'}
        onClose={close}
        title={t('sales.detail.cancelTitle')}
        description={
          sale.status === 'delivered' ? (
            <>
              <Text>{t('sales.detail.cancelDelivered')}</Text>
              <Checkbox
                label={t('sales.detail.confirmReturn')}
                checked={confirmReturn}
                onChange={(e) => setConfirmReturn(e.target.checked)}
              />
            </>
          ) : (
            t('sales.detail.cancelText')
          )
        }
        confirmLabel={t('sales.detail.cancel')}
        pending={action.isPending}
        error={action.error ? errorMessage(action.error) : undefined}
        onConfirm={() =>
          run(
            { id: sale.id, action: 'cancel', confirm_return: confirmReturn },
            t('sales.detail.cancelled'),
          )
        }
      />
    </div>
  );
}
